import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FieldTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DatetimeInputComponent } from '../datetime-input.component';
import { distinctUntilChanged, startWith, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FormlyErrorComponent } from '../formly-error.component';
import { DateHelperService } from '../../services/date-helper.service';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';
import { commitUserValue } from './user-edit.util';
import { composeDateTime, dayOf, findDateSource } from './date-from-field.util';
import { RouteSectionSlot } from './route-section-slot.service';

@Component({
  selector: 'formly-field-datetime',
  template: `
    <div class="mb-1">
      <div class="dd-clear-field">
        <app-datetime-input
          #datetimeInput
          class="block w-full"
          [label]="props.label"
          [required]="props.required"
          [isDisabled]="props.disabled"
          [dateTime]="dateFromSource ? timeOnlyValue() : field.formControl.value"
          [hideDate]="props.hideDate || props.showOnlyTime || dateFromSource"
          [timeStyle]="dateFromSource || (inRouteSection() && (props.showOnlyTime || props.hideDate)) ? 'leading' : 'centered'"
          [hideTime]="props['hideTime']"
          [hideTimelabel]="props.hideTimelabel"
          [info]="props.info"
          [formlyDataCy]="field"
          [timeBadge]="props['ddTimeBadge']"
          [timeBadgeDismissLabel]="props['ddTimeBadgeDismissLabel']"
          (timeBadgeDismiss)="props['ddTimeBadgeDismiss']?.()"
          (dateTimeChange)="onDateTimeChange($event)">
          <app-clear-field-button
            datetimeTrailingAction
            [field]="field"
            [hasValue]="hasClearableValue()"
            [disabled]="isDisabled()"
            [ariaLabel]="clearAriaLabel()"
            [focusTarget]="datetimeInput"
            (clearSelection)="clear()" />
        </app-datetime-input>
      </div>
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    DatetimeInputComponent,
    MatInputModule,
    MatDatepickerModule,
    FormlyDataCyDirective,
    FormlyErrorComponent,
    ClearFieldButtonComponent,
  ],
})
export class FormlyFieldDateTime extends ClearableFieldType implements OnInit {
  cd = inject(ChangeDetectorRef);
  /** Inside a route section (Figma route card) every time-only field gets the clock, not just a dateFromField one. */
  readonly #routeSection = inject(RouteSectionSlot, { optional: true });
  protected readonly inRouteSection = computed(() => !!this.#routeSection?.active());

  /**
   * A time-only box clears what it shows: its control can already hold the day (a `dateFromField`
   * source, a series start) while the time is still empty, and the × then sat on the spot where the
   * empty box shows its clock.
   */
  override hasClearableValue(): boolean {
    const timeOnly = !!(this.props.hideDate || this.props.showOnlyTime || this.dateFromSource);
    if (!timeOnly) return super.hasClearableValue();
    const shown = this.dateFromSource ? this.timeOnlyValue() : this.formControl.value;
    return !!DateHelperService.dateTimeToDateAndTime((shown as string | null) ?? null).time;
  }

  /**
   * A time typed before its day is chosen lives only here (the control stays null), so clearing
   * the control alone is a no-op: the box kept the time and the next date pick brought it back.
   */
  override clear(): void {
    this.#time.set(null);
    this.#value.set(null);
    super.clear();
  }
  destroy$ = injectDestroy();
  constructor() {
    super();
  }

  /**
   * The inner datetime input is not a ControlValueAccessor for this field's control — it emits a
   * value we write ourselves — so nothing marks the control dirty unless we do (#21026). This also
   * covers `date_range`/`week`, whose start/end children are datetime fields.
   */
  onDateTimeChange(value: unknown): void {
    if (this.dateFromSource) {
      this.#time.set(DateHelperService.dateTimeToDateAndTime((value as string | null) ?? null).time);
      this.#composing = true;
      try {
        commitUserValue(this.formControl, composeDateTime(this.#sourceDay(), this.#time()));
      } finally {
        this.#composing = false;
      }
      return;
    }
    commitUserValue(this.formControl, value);
  }

  /**
   * `props.dateFromField`: this field shows only a time and takes its day from another date field,
   * so a form can ask for the date in one place and the time in another while the model keeps one
   * datetime. Without a day it stores nothing — a time alone would otherwise land on today — so
   * a required field stays invalid until the day is chosen (Formly's own `required` on the empty
   * value); the date field shows why — so configure that source field as required too, or the
   * error has no visible cause.
   * Plain fields, not signals: props are static per field instance and this is set in ngOnInit,
   * before the first render.
   */
  protected dateFromSource = false;
  #sourceField: FormlyFieldConfig | undefined;
  /** The entered time, kept while there is no day to put it on (the control is empty then). */
  readonly #time = signal<string | null>(null);
  /** True while the source subscription writes the control, so its null is not read as an external clear. */
  #composing = false;
  /**
   * A day this field got from elsewhere — a loaded order, the route rolling stop 2 onto the next
   * day — holds until the source's day moves, so a time edit there does not snap the stop back
   * onto the source's first day.
   */
  #keepOwnDay = false;
  readonly #value = signal<unknown>(null);
  /** What the time-only input shows: the stored value, or the entered time on a stand-in day. */
  protected readonly timeOnlyValue = computed(() => {
    const time = this.#time();
    return this.#value() ?? (time ? composeDateTime(DateHelperService.dateObjectToDateString(new Date()), time) : null);
  });

  /**
   * The day the time lands on. A hidden source is owned by something else (a repeating series
   * leads the date then), so the field keeps its own day. One rule for both the source
   * subscription and a time edit, so the two never disagree.
   */
  #sourceDay(): string | null {
    if (this.#sourceField?.hide) return dayOf(this.formControl.value);
    return (this.#keepOwnDay && dayOf(this.formControl.value)) || dayOf(this.#sourceField?.formControl?.value);
  }

  #wireDateSource(path: string): void {
    this.#sourceField = findDateSource(this.field, path);
    const source = this.#sourceField?.formControl;
    if (!source) {
      console.warn(`[datetime] dateFromField '${path}' not found for '${String(this.field.key)}'`);
      return;
    }
    this.dateFromSource = true;
    const own = DateHelperService.dateTimeToDateAndTime(this.formControl.value ?? null);
    this.#time.set(own.time);
    this.#value.set(this.formControl.value);
    this.#keepOwnDay = !!own.date;
    // A loaded order carries its departure but not the form-only date field: seed the day from it
    // rather than wiping the departure on the first sync. Not a user edit, so not dirty. It does
    // emit: Formly syncs the model from valueChanges, so a silent write would leave the model empty.
    // Not into a hidden source: that one is meant to stay empty (the series leads the day then).
    if (own.date && !this.#sourceField?.hide && !dayOf(source.value)) {
      source.setValue(DateHelperService.toLocalISOString(new Date(`${own.date}T00:00:00`)));
    }
    // Only a change of DAY recomposes a value that has its own day: a multi-day order loads stop 2
    // on the next day while the source holds the first, and a whole-form patch re-emits the same
    // source value without anyone picking a new date.
    let lastDay: string | null | undefined;
    source.valueChanges.pipe(startWith(source.value), takeUntil(this.destroy$)).subscribe(() => {
      const day = dayOf(source.value);
      if (lastDay !== undefined && day !== lastDay) this.#keepOwnDay = false;
      lastDay = day;
      const next = this.#keepOwnDay ? this.formControl.value : composeDateTime(this.#sourceDay(), this.#time());
      if (next !== this.formControl.value) {
        // Our own write: losing the day empties the control but keeps the typed time.
        this.#composing = true;
        try {
          this.formControl.setValue(next);
        } finally {
          this.#composing = false;
        }
      }
      // During ngOnInit this runs before the value subscription below exists.
      this.#value.set(this.formControl.value);
    });
  }

  ngOnInit() {
    // if (this.props?.useCurrentDate && !this.field.formControl.value) {
    //   this.field.formControl.setValue(DateHelperService.roundHours(new Date()));
    // }
    const dateFromField = this.props?.['dateFromField'] as string | undefined;
    if (dateFromField) this.#wireDateSource(dateFromField);

    // Time-only fields (showOnlyTime/hideDate) keep a full datetime value, so a
    // prefilled date — or leftover value after the time is cleared — satisfies
    // Formly's value-based `required` validator even when no time is entered. The
    // form then looks valid and the save reaches the backend with a missing time,
    // which crashes server-side. Enforce that a time component is actually present.
    //
    // Read the CONFIGURED setting, not the live one. The route field toggles hideDate at
    // runtime and remembers that choice per user, so by the time this runs the live props may
    // already say "show the date" — and the validator would never be attached. It stamps
    // ddTimeOnlyConfigured before its first mutation; everywhere else that key is absent and
    // the live props are the configured ones.
    const timeOnlyConfigured =
      (this.props?.['ddTimeOnlyConfigured'] as boolean | undefined) ??
      !!(this.props?.showOnlyTime || this.props?.hideDate || this.dateFromSource);

    if (timeOnlyConfigured) {
      this.field.formControl.addValidators(this.#requiredTimeValidator);
      this.field.formControl.updateValueAndValidity({ emitEvent: false });
    }

    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(value => {
      this.#value.set(value);
      if (this.dateFromSource && value) {
        // A value written from outside (prefill, entry loader) brings its own time. A bare day — a
        // repeating series writes only the day while no time is stored — keeps the typed time. The
        // re-composed write re-enters this handler once, now with a time, which ends the loop.
        const { date, time } = DateHelperService.dateTimeToDateAndTime(value);
        if (!this.#composing) {
          const own = dayOf(value);
          this.#keepOwnDay = !!own && own !== dayOf(this.#sourceField?.formControl?.value);
        }
        if (time) this.#time.set(time);
        else if (date && this.#time()) this.formControl.setValue(composeDateTime(date, this.#time()));
      } else if (this.dateFromSource && !this.#composing) {
        // Cleared from outside (an action, a reset): the next date pick must not bring the old time back.
        this.#time.set(null);
      }
      this.cd.markForCheck();
    });
  }

  readonly #requiredTimeValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!this.props?.required || this.props?.disabled) return null;
    const { time } = DateHelperService.dateTimeToDateAndTime(control.value ?? null);
    return time ? null : { required: true };
  };
}
